import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Star, Quote, Play } from 'lucide-react'
import { testimonialsAPI } from '@utils/api'

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchTestimonials()
  }, [])

  const fetchTestimonials = async () => {
    try {
      const response = await testimonialsAPI.getFeatured(6)
      setTestimonials(response.data)
      setLoading(false)
    } catch (error) {
      console.error('Failed to fetch testimonials:', error)
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <section className="section-padding bg-gray-50 dark:bg-gray-950">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Student Success Stories
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-gray-200 dark:bg-gray-800 h-48 rounded-lg"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="section-padding pb-24 bg-white dark:bg-gray-900">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Student Success Stories
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Hear from our alumni who transformed their careers with Deoriginlabs
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial._id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="card card-hover p-6"
            >
              <div className="flex items-center mb-4">
                <Quote className="w-8 h-8 text-primary-200" />
                <div className="flex ml-auto">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < testimonial.rating
                          ? 'text-yellow-500 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>

              <p className="text-gray-700 dark:text-gray-300 mb-6 line-clamp-4">
                "{testimonial.content}"
              </p>

              <div className="flex items-center">
                <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full mr-4"></div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    {testimonial.studentName}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {testimonial.course?.courseName}
                  </p>
                  {testimonial.placementDetails?.placed && (
                    <p className="text-sm text-green-600 font-medium">
                      Placed at {testimonial.placementDetails.company}
                    </p>
                  )}
                </div>
              </div>

              {testimonial.videoUrl && (
                <button className="mt-4 flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
                  <Play className="w-4 h-4 mr-2" />
                  Watch Story
                </button>
              )}
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <button className="btn btn-outline btn-md">
            View All Success Stories
          </button>
        </div>
      </div>
    </section>
  )
}

export default TestimonialsSection
